/**
 * Klaviyo Email Click-Through Identity Resolution
 *
 * When a user clicks through from a Klaviyo email, the URL contains ?mbg_pid=<klaviyo_profile_id>.
 * This utility resolves that ID to a known user identity via a Netlify function (which queries
 * Segment Profile API first, then Klaviyo API as fallback), then calls analytics.identify()
 * to tie the anonymous browsing session to the known user profile.
 */

const SESSION_KEY_PREFIX = "mbg_identified_"

export const identifyEmailUser = async (location) => {
  try {
    const params = new URLSearchParams(location.search)
    const mbgPid = params.get("mbg_pid")

    if (!mbgPid) return

    // Check sessionStorage to avoid duplicate lookups within the same session
    const sessionKey = `${SESSION_KEY_PREFIX}${mbgPid}`
    if (typeof window !== "undefined" && window.sessionStorage.getItem(sessionKey)) {
      return
    }

    const response = await fetch(`/api/account/identify-email-user?mbg_pid=${encodeURIComponent(mbgPid)}`)

    if (!response.ok) {
      console.log(`[identifyEmailUser] API returned ${response.status}`)
      return
    }

    const { userId, traits } = await response.json()

    if (!traits) return

    // Only call analytics.identify if the Segment snippet is loaded
    if (!window.analytics) return

    const identifyTraits = {
      ...(traits.email && { email: traits.email }),
      ...(traits.first_name && { first_name: traits.first_name }),
      ...(traits.last_name && { last_name: traits.last_name }),
      klaviyo_id: mbgPid,
    }

    const identifyOptions = {
      externalIds: [
        {
          id: mbgPid,
          type: "klaviyo_id",
          collection: "users",
          encoding: "none",
        },
      ],
    }

    if (userId) {
      window.analytics.identify(userId, identifyTraits, identifyOptions)
    } else {
      window.analytics.identify(identifyTraits, identifyOptions)
    }

    // Mark as identified for this session
    try {
      window.sessionStorage.setItem(sessionKey, "true")
    } catch (err) {
      // sessionStorage may be unavailable
    }
  } catch (err) {
    console.error("[identifyEmailUser]", err)
  }
}
